import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";
import { EdgeDevice } from "./edgeDeviceInterfaces";
import { getEdgeDeviceWireguardTunnelLabel } from "./edgeDeviceFilterUtils";
import { getComputedTunnelStatus } from "./wireGuardTunnelStatusUtils";
import { WireGuardTunnel } from "./wireGuardTunnelsInterfaces";
import { findMatchingWireguardTunnel } from "./edgeDeviceTunnelLinkUtils";
import { WireguardTunnelNameLink } from "./WireguardTunnelNameLink";

interface Props {
  open: boolean;
  selectedDevice: EdgeDevice | null;
  onClose: () => void;
  wireguardTunnels: WireGuardTunnel[];
  onOpenWireguardTunnel: (tunnel: WireGuardTunnel) => void;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography sx={{ fontFamily: "Mulish", fontSize: 12, color: "#5f6368" }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Mulish",
        fontSize: 14,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const displayValue = (value: string | null | undefined) => {
  if (value == null || value === "") {
    return "-";
  }
  return value;
};

const formatLastHandshake = (lastHandshake: string | null | undefined) => {
  if (!lastHandshake) {
    return "Never";
  }

  const parsed = new Date(lastHandshake);
  if (!Number.isFinite(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleString("en-US");
};

export const EdgeDeviceDetailsDrawer = ({
  open,
  selectedDevice,
  onClose,
  wireguardTunnels,
  onOpenWireguardTunnel,
}: Props) => {
  const tunnel = selectedDevice?.tunnel;
  const matchedTunnel = selectedDevice
    ? findMatchingWireguardTunnel(selectedDevice, wireguardTunnels)
    : null;
  const tunnelName = selectedDevice
    ? getEdgeDeviceWireguardTunnelLabel(selectedDevice)
    : "";

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontFamily: "Mulish", fontSize: 22, fontWeight: 700 }}>
            Edge Device Details
          </Typography>
          <Button
            onClick={onClose}
            sx={{ fontFamily: "Mulish", textTransform: "none" }}
          >
            Close
          </Button>
        </Stack>
        <Divider sx={{ my: 2 }} />
        {selectedDevice ? (
          <Stack gap={3}>
            <Row
              label="Serial Number"
              value={displayValue(selectedDevice.serial_number)}
            />
            <Row
              label="Mac Address"
              value={displayValue(selectedDevice.mac_address)}
            />
            <Row
              label="Manufacturer"
              value={displayValue(selectedDevice.manufacturer)}
            />
            <Row label="Sku" value={displayValue(selectedDevice.sku)} />
            <Row
              label="Provisioning State"
              value={displayValue(selectedDevice.provisioning_state)}
            />
            <Divider />
            <Typography sx={{ fontFamily: "Mulish", fontSize: 16, fontWeight: 700 }}>
              Wireguard Tunnel Details
            </Typography>
            <Box>
              <Typography sx={{ fontFamily: "Mulish", fontSize: 12, color: "#5f6368" }}>
                Wireguard Tunnel Name
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontFamily: "Mulish",
                  fontSize: 14,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                <WireguardTunnelNameLink
                  tunnelName={tunnelName}
                  matchedTunnel={matchedTunnel}
                  onOpenTunnel={onOpenWireguardTunnel}
                />
              </Typography>
            </Box>
            <Row
              label="Firmware Version"
              value={displayValue(selectedDevice.firmware_version)}
            />
            <Row label="Site Name" value={displayValue(selectedDevice.site_name)} />
            <Row
              label="Deployment Location"
              value={displayValue(selectedDevice.deployment_location)}
            />
            <Row
              label="Wireguard Tunnel Status"
              value={getComputedTunnelStatus(tunnel?.last_handshake ?? null)}
            />
            <Row
              label="Last Handshake"
              value={formatLastHandshake(tunnel?.last_handshake)}
            />
            <Row
              label="Client Tunnel IP"
              value={displayValue(tunnel?.client_tunnel_ip)}
            />
            <Row label="Server Name" value={displayValue(tunnel?.server_name)} />
          </Stack>
        ) : (
          <Typography sx={{ fontFamily: "Mulish" }}>
            Select an edge device to view details.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};
